import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { possessive,
  CarePhase,
  CareEnvironment,
  carePhaseDefs,
  careEnvironmentsApplicableToPhase,
  pickClientCareEnvironmentCosts,
  hasSeparateRoomAndBoard,
  resolveUseClaimProjectionEngine,
  usesFamilyCaregivingStep,
} from '@shared';
import {
  previewClientUpdates,
  putCareEnvironmentSelectionsForClientByClientIdRequest,
  selectClient,
} from '@/model';

export function useCareSettingsSlide(carePhase: CarePhase) {
  const client = useSelector(selectClient)!;

  const {
    clientFirstName,
    intakeSurvey: { clientBirthYear, clientCurrentCarePhase, clientPhaseStartAges },
    phaseCosts,
    clientPhasePredictedStartYears,
    clientPhaseInflationFactors,
  } = client;

  const dispatch = useDispatch();
  const [selectedCareSetting, setSelectedCareSetting] = useState<number | null>(
    client.careEnvironmentSelections?.[carePhase] ?? null,
  );

  const carePhaseIndex = carePhaseDefs[carePhase].index;
  const selectedCarePhaseCosts = phaseCosts[carePhaseIndex];
  const predictedPhaseStartYear = clientPhasePredictedStartYears[carePhase];
  const phaseAge = predictedPhaseStartYear
    ? predictedPhaseStartYear - clientBirthYear
    : clientPhaseStartAges[carePhase]!;
  const carePhaseDurationMonths = selectedCarePhaseCosts.phaseCareMonthsNeeded;
  const applicableCareSettings = careEnvironmentsApplicableToPhase[carePhase];
  const carePhaseStartAge = clientPhaseStartAges[carePhase];
  const isDurationCustom = selectedCarePhaseCosts.isDurationCustom;
  const isCareSettingHome = selectedCareSetting === CareEnvironment.home;
  const phaseInflationFactor = clientPhaseInflationFactors[carePhase]!;

  const handleButtonClick = (careSettingDbId: number) => {
    const isDeselecting = careSettingDbId === selectedCareSetting;
    const nextSelections = {
      ...(client.careEnvironmentSelections ?? {}),
      [carePhase]: isDeselecting ? null : careSettingDbId,
    };

    setSelectedCareSetting(isDeselecting ? null : careSettingDbId);

    dispatch(
      previewClientUpdates({ ...client, careEnvironmentSelections: nextSelections }),
    );

    dispatch(
      putCareEnvironmentSelectionsForClientByClientIdRequest({
        clientId: client.clientId,
        careEnvironmentSelections: nextSelections,
      }),
    );
  };

  const useClaimProjectionEngine = resolveUseClaimProjectionEngine(client);

  // Home always runs the family-caregiving step; Independent Living does too
  // under the claim-projection engine. Shared with the slide-sequence filter
  // (`usesFamilyCaregivingStep`) so the routing and the active-slide list agree —
  // otherwise the sequence filter drops the slide and this override is rejected.
  const routesToFamilyCaregiving = usesFamilyCaregivingStep(
    selectedCareSetting as CareEnvironment,
    useClaimProjectionEngine,
  );

  const careSettingCosts = applicableCareSettings.map(setting => {
    const env = setting.dbId as CareEnvironment;
    const isHome = env === CareEnvironment.home;
    // Independent Living under the claim-projection engine is priced additively:
    // home-style care (policy-eligible) + out-of-pocket room & board. Every
    // other setting keeps its single home/facility figure, so this only
    // diverges for the IL card under the new engine.
    const isRoomAndBoardSplit = useClaimProjectionEngine && hasSeparateRoomAndBoard(env);
    const cost = isHome
      ? calcInflatedMaximumFamilyShareCostFor(selectedCarePhaseCosts, client, phaseInflationFactor)
      : isRoomAndBoardSplit
        ? calcInflatedIndependentLivingCostFor(selectedCarePhaseCosts, client, phaseInflationFactor)
        : calcInflatedProfessionalShareCostFor(
            selectedCarePhaseCosts,
            client,
            phaseInflationFactor,
            env,
          );
    return { dbId: setting.dbId, cost, isHome };
  });

  return {
    client,
    clientFirstName,
    clientCurrentCarePhase,
    carePhaseIndex,
    selectedCarePhaseCosts,
    phaseAge,
    carePhaseDurationMonths,
    applicableCareSettings,
    carePhaseStartAge,
    isDurationCustom,
    selectedCareSetting,
    isCareSettingHome,
    routesToFamilyCaregiving,
    handleButtonClick,
    careSettingCosts,
    nextButtonLabel: determineNextSlideLabel(routesToFamilyCaregiving, carePhase, clientFirstName),
  };
}

function determineNextSlideLabel(
  routesToFamilyCaregiving: boolean,
  carePhase: CarePhase,
  clientFirstName: string,
) {
  if (routesToFamilyCaregiving) {
    return `Next`;
  }

  return carePhaseDefs[carePhase].index === 2
    ? `Review ${possessive(clientFirstName)} Costs`
    : `Review ${carePhaseDefs[carePhase + 1].label}`;
}

function calcInflatedMaximumFamilyShareCostFor(
  phaseCosts: SinglePhaseCosts,
  client: Client,
  phaseInflationFactor: number,
) {
  const careEnvironmentCostData = pickClientCareEnvironmentCosts(client, CareEnvironment.home);

  return (
    careEnvironmentCostData.rateAmount * phaseCosts.phaseCareHoursNeeded * phaseInflationFactor
  );
}

function calcInflatedProfessionalShareCostFor(
  phaseCosts: SinglePhaseCosts,
  client: Client,
  phaseInflationFactor: number,
  careEnvironment: CareEnvironment,
) {
  const careEnvironmentCostData = pickClientCareEnvironmentCosts(client, careEnvironment);

  return (
    careEnvironmentCostData.rateAmount * phaseCosts.phaseCareMonthsNeeded * phaseInflationFactor
  );
}

// Independent Living total under the claim-projection engine: home-style care
// (professional hours × the home hourly rate, policy-eligible) plus out-of-pocket
// room & board (the IL monthly rate over the phase). Uses
// `phaseProfessionalCareHoursProvided` — needed minus any family-provided hours —
// to match the engine's `phaseProfessionalShareCost` so this preview card equals
// the phase-summary cost the client sees after selecting IL. (Under the v1
// no-IL-family-offset default the two hour figures are equal.)
function calcInflatedIndependentLivingCostFor(
  phaseCosts: SinglePhaseCosts,
  client: Client,
  phaseInflationFactor: number,
) {
  const homeCostData = pickClientCareEnvironmentCosts(client, CareEnvironment.home);
  const roomAndBoardCostData = pickClientCareEnvironmentCosts(
    client,
    CareEnvironment.independentLiving,
  );

  const inflatedCareCost =
    homeCostData.rateAmount * phaseCosts.phaseProfessionalCareHoursProvided * phaseInflationFactor;
  const inflatedRoomAndBoardCost =
    roomAndBoardCostData.rateAmount * phaseCosts.phaseCareMonthsNeeded * phaseInflationFactor;

  return inflatedCareCost + inflatedRoomAndBoardCost;
}
